import { useState, useEffect, useRef, useCallback, memo } from "react";
import ConfettiExplosion from "react-confetti-explosion";
import { Badge, Button, Grid, Typography, Box, keyframes } from "@mui/material";
import {
  IconAlertTriangle,
  IconFlame,
  IconFlameFilled,
} from "@tabler/icons-react";

import CurrentSession from "data/userSession";
import AnalyticsService from "data/firestore/analytics";
import StreakUtils, { StreakState } from "utils/streak";
import TimeUtils from "utils/time";
import Logger from "utils/logger";

export interface UserData {
  id?: string;
  uid?: string;
  streak?: number;
  lastTugged?: Date | string;
  active?: boolean;
}

export interface StreakProps {
  user?: UserData;
  isCurrentUser?: boolean;
  clickable?: boolean;
  variant?: "full" | "compact" | "display-only";
  /** Custom click handler. If provided, this is called instead of navigation. */
  onPress?: (streak: number, streakInDanger: boolean) => void;
}

const Streak = ({
  user,
  isCurrentUser = false,
  clickable = false,
  variant = "full",
  onPress,
}: StreakProps) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [currentUser, setCurrentUser] = useState<UserData | null>(
    isCurrentUser ? (CurrentSession.user as any) : null
  );
  const [streak, setStreak] = useState(0);
  const [previousStreak, setPreviousStreak] = useState(0);
  const [streakInDanger, setStreakInDanger] = useState(false);
  const [streakWasInDanger, setStreakWasInDanger] = useState(false);

  // Use a ref to track the current state values to avoid stale closures in the subscription callback
  const streakStateRef = useRef<StreakState>({
    streak: 0,
    streakInDanger: false,
    streakWasInDanger: false,
    timestamp: 0,
    userId: undefined,
  });

  // Get the actual user object to use
  const targetUser = isCurrentUser ? currentUser : user;

  // Define a gentle pulse animation for the streak icon
  const pulseAnimation = keyframes`
    0% {
      opacity: .9;
      transform: scale(1);
    }
    50% {
      opacity: 1;
      transform: scale(1.1);
    }
    100% {
      opacity: .9;
      transform: scale(1);
    }
  `;

  // Track if this is the first update to prevent celebration on load
  const isFirstLoad = useRef(true);

  const updateStreak = useCallback((userObj: UserData) => {
    const currentState = streakStateRef.current;
    const state = StreakUtils.extractState(userObj, currentState);

    Logger.debug(
      `[Streak] updateStreak — first=${isFirstLoad.current}, incoming=${state.streak}, current=${currentState.streak}, userStreak=${userObj.streak}`
    );

    if (
      !isFirstLoad.current &&
      state.streak === currentState.streak &&
      state.streakInDanger === currentState.streakInDanger &&
      state.streakWasInDanger === currentState.streakWasInDanger
    ) {
      return;
    }

    if (isFirstLoad.current) {
      Logger.debug(
        `[Streak] First load — previousStreak set to ${state.streak}`
      );
      setPreviousStreak(state.streak);
      isFirstLoad.current = false;
    } else {
      Logger.debug(
        `[Streak] Update — previousStreak set to ${currentState.streak}`
      );
      setPreviousStreak(currentState.streak);
    }

    setStreak(state.streak);
    setStreakInDanger(state.streakInDanger);
    const newStreakWasInDanger =
      state.streakInDanger || state.streakWasInDanger;
    setStreakWasInDanger(newStreakWasInDanger);

    streakStateRef.current = {
      streak: state.streak,
      streakInDanger: state.streakInDanger,
      streakWasInDanger: newStreakWasInDanger,
      timestamp: state.timestamp,
      userId: state.userId,
    };
  }, []);

  useEffect(() => {
    async function handleUpdate() {
      if (isCurrentUser) {
        // streak/lastTugged are Supabase-authoritative; re-source them before the UI
        // update so a save doesn't briefly flip the streak to "in danger".
        await CurrentSession.user.refreshStreak();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        updateStreak(CurrentSession.user as any);
      }
    }

    if (typeof window !== "undefined") {
      window.addEventListener("userUpdated", handleUpdate);
    }
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("userUpdated", handleUpdate);
      }
    };
  }, [isCurrentUser, updateStreak]);

  useEffect(() => {
    if (isCurrentUser) {
      CurrentSession.load().then((currentUserObj) => {
        const ud = currentUserObj as unknown as UserData;
        setCurrentUser(ud);
        updateStreak(ud);
      });
    } else if (user) {
      // Use the provided user object directly
      updateStreak(user);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCurrentUser, user, updateStreak]);

  // Midnight Refresh: Ensure streak status updates exactly at midnight
  useEffect(() => {
    if (!targetUser) return;

    const msUntilMidnight = TimeUtils.untilMidnight() + 1000; // Add 1s buffer
    const timeout = setTimeout(() => {
      updateStreak(targetUser);
    }, msUntilMidnight);

    return () => clearTimeout(timeout);
  }, [targetUser, updateStreak]);

  if (!targetUser || (targetUser.streak ?? 0) <= 0) {
    return null;
  }

  // Hide streaks entirely if the user has disabled them
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((CurrentSession.user as any).streakDisabled) {
    return null;
  }

  const RenderedIcon =
    streak > 0 && !streakInDanger ? IconFlameFilled : IconFlame;

  // Compact version for use in UserCard
  if (variant === "compact") {
    return (
      <Grid
        container
        alignItems="center"
        style={{ flex: 0, flexBasis: "fit-content", marginRight: 15 }}
      >
        <Grid item>
          <Typography
            style={{
              color: StreakUtils.getColor(streak, streakInDanger),
              fontWeight: "bold",
              fontSize: 18,
            }}
          >
            {StreakUtils.formatDisplay(streak)}
          </Typography>
        </Grid>
        <Grid item>
          <Badge
            badgeContent={
              streakInDanger && streak > 0 ? (
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    animation: `${pulseAnimation} 1.5s ease-in-out infinite`,
                  }}
                >
                  <IconAlertTriangle size={18} style={{ color: "orange" }} />
                </Box>
              ) : (
                0
              )
            }
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          >
            {streak > 0 && !streakInDanger ? (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  animation: `${pulseAnimation} 1.5s ease-in-out infinite`,
                }}
              >
                <RenderedIcon size={24} color="orangered" />
              </Box>
            ) : (
              <RenderedIcon
                size={24}
                color={streak > 0 ? "orangered" : "gray"}
              />
            )}
          </Badge>
        </Grid>
      </Grid>
    );
  }

  // Full version - just the streak display part
  else {
    return (
      <Button
        variant="text"
        onClick={
          clickable
            ? () => {
                AnalyticsService.event("click_streak_button_in_header");
                if (onPress) {
                  onPress(streak, streakInDanger);
                }
              }
            : undefined
        }
      >
        <Grid item>
          <Typography
            variant="h2"
            style={{
              color: StreakUtils.getColor(streak, streakInDanger),
            }}
          >
            {`${Math.max(streak, 0).toLocaleString()}d`}
          </Typography>
          {StreakUtils.shouldCelebrate(
            streak,
            streakInDanger,
            streakWasInDanger,
            previousStreak
          ) ? (
            <ConfettiExplosion particleCount={400} />
          ) : null}
        </Grid>
        <Grid item>
          <Badge
            badgeContent={
              streakInDanger && streak > 0 ? (
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    animation: `${pulseAnimation} 1.5s ease-in-out infinite`,
                  }}
                >
                  <IconAlertTriangle
                    size={15}
                    style={{ color: "orange", marginRight: 5 }}
                  />
                </Box>
              ) : (
                0
              )
            }
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          >
            {streak > 0 && !streakInDanger ? (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  animation: `${pulseAnimation} 1.5s ease-in-out infinite`,
                }}
              >
                <RenderedIcon stroke={1.5} size="1.5rem" color="orangered" />
              </Box>
            ) : (
              <RenderedIcon
                stroke={1.5}
                size="1.5rem"
                color={streak > 0 ? "orangered" : "gray"}
              />
            )}
          </Badge>
        </Grid>
      </Button>
    );
  }
};

// Memoized: the header re-renders every second (streak-danger countdown ticks),
// but the streak itself rarely changes. With stable props this skips those
// per-second re-renders; it still updates via its own state on real changes.
export default memo(Streak);
